import {Component, OnInit, HostListener} from "@angular/core";
import {Scilearn} from "../constants";
import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/filter";
import "rxjs/add/operator/subscribeOn";
import {
    ActivityModalEvent, ACTIVITY_STATUS, ActivityEventData, ReportEventData
} from "../event/ActivityEvent";
import {RecordingBadge} from "../services/usermodel/usermodel.objects";
import {ContentCursor} from "../services/content/content.cursor";
import {ComponentBase} from "../base/ComponentBase";
import {ReportsService} from "../services/reports/reports.service";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ReviewWord, ISelectionReport} from "./reports.objects";
import {UtilitiesService} from "../services/utilities.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {DisplayAgent, WindowSize} from "../services/browser/display.agent";
import {PreviewReportItems, FluencyReportItems, QuizReportItems} from "./reports.diagrams";
import {SelectionReportSummaryStatus, ISelectionReportRecordingStatus} from "./selection-report-summary-status";
import {SelectionReportAudio} from "./selection-report-audio";
import {TranslateService} from "@ngx-translate/core";
import {ContentCssClass} from "../content/content-view.objects";
import {ReviewWordColor} from "../services/usermodel/usermodel.objects";
import {ContentFormatter} from "../content/content.formatter";
import {RaVersion} from "../services/configuration/ra.version";
import {Login} from "../login/login";
import {ISelectionDescription} from "../library/library.objects";
import {PleaseWaitEvent, PleaseWaitTarget} from "../event/ContentEvents";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {filter} from "rxjs/operators";
import { ClientConfiguration } from "../services/configuration/client.config";

@Component({
    selector: "selection-modal",
    templateUrl: "./reports.selection.modal.component.html"
})

export class SelectionModalComponent extends ComponentBase implements OnInit {

    private modalType:string = Scilearn.SELECTION_MODAL;
    private isAutomatic:boolean;
    private SECTIONS = ["preview", "fluency", "quiz"];
    public report:ISelectionReport;
    public activateModal:boolean = false;
    public learnerName:string = "";
    public maxPoint:number = 0;
    public earnedPoint:number = 0;
    public format:string = "modal";
    public completedDate: string;
    public windowSize: WindowSize;
    public WindowSizeEnum = WindowSize; // so we can access the enum in the html
    public preview: PreviewReportItems;
    public fluency: FluencyReportItems;
    public quiz: QuizReportItems;
    public summaryStatus: ISelectionReportRecordingStatus;
    public reviewWordsRange: Array<boolean>;

    constructor(private modalConfig:ModalConfig,
                private eventBus:EventBus,
                private contentCursor:ContentCursor,
                private reportsService:ReportsService,
                private log:BugsnagLoggingService,
                private displayAgent:  DisplayAgent,
                private selectionReportSummaryStatus: SelectionReportSummaryStatus,
                private selectionReportAudio: SelectionReportAudio,
                private translateService: TranslateService,
                private login: Login,
                private audioQueueService: AudioQueueService,
                private config: ClientConfiguration) {
        super("SelectionModalComponent");
    }

    ngOnInit(): void {
        this.setupListeners();
        this.determineWindowSize();
    }

    @HostListener("window:resize", ["$event"])
    public onResize(event): void {
        this.windowSize = this.displayAgent.calculateSize(event.target.innerWidth);
        console.log("Window size = " + this.windowSize);
    }

    private determineWindowSize(): void {
        this.windowSize = this.displayAgent.getWindowSizeFromScreenWidth();
        console.log("Window size = " + this.windowSize);
    }

    public printReport(): void {
        this.reportsService.printReport("selection-main-content");
    }

    public openModal(event: ReportEventData): void {
        let learnerName: string = this.login.name;
        this.audioQueueService.reset().subscribe(() => {
            if (event.data.storyLoaded) {
                this.eventBus.next(new PleaseWaitEvent({show: true, soft: true, audio: true, destination: PleaseWaitTarget.READING}));
                this.showReport(this.contentCursor.selection.referenceStory(), learnerName);
            } else {
                this.showReport(event.storyUniqName, learnerName);
            }
        });
    }

    public closeModal(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.activateModal = false;
            this.modalConfig.removeModal(new ActivityEventData({ type: this.modalType }));
            if (this.isAutomatic) {
                this.selectionReportAudio.playExitAudio();
            }
        });
    }

    public determineSummaryStatus(): void {
        this.summaryStatus = this.selectionReportSummaryStatus.determineSummaryStatus(
            this.quiz.hasStar, this.report.fluency.recordingBadge
        );
    }

    public showReport(title:string, learnerName:string):void {
        this.learnerName = learnerName;
        this.log.log("retrieving selection report for: " + title);
        let sub = this.reportsService.retrieveSelectionReport(title)
            .subscribe(output => {
                this.report = output.SelectionReport;
                this.getSectionInfo();
                this.determineSummaryStatus();
                this.activateModal = true;
                if (this.isAutomatic) {
                    this.selectionReportAudio.playInitalAudio(this.summaryStatus, this.fluency.reviewWords.length > 0);
                }
                this.reviewWordsRange = this.reportsService.getTableListRange();
                this.eventBus.next(new PleaseWaitEvent({show: false, audio: false, destination: PleaseWaitTarget.READING}));
            }, () => {
                this.modalConfig.openMessageModal("exit_while_offline");
            });
        this.subscriptions.push(sub);
    }

    public getRecordingStatusImg(): string {
        return this.summaryStatus.img;
    }

    public getRecordingStatusText(): string {
        let statusText:string = "";
        this.translateService.get(this.summaryStatus.localizationKey).subscribe((res) => {
            statusText = res;
        });
        return statusText;
    }

    public getBarFill(key: string): {"height": string, "margin-top": string, "z-index": number} {
        let ratio: number;
        let backGroundBarHeight: number = 120;
        let zIndex: number = 99999;

        if (key === "quiz") {
            ratio = this.quiz.point / this.quiz.goal;
            zIndex += 100;
        } else if (key === "fluency") {
            ratio = this.fluency.point / this.fluency.goal;
            zIndex += 50;
        } else if (key === "preview") {
            ratio = this.preview.point / this.preview.goal;
        }

        let barHeight: string = String(backGroundBarHeight * ratio) ;

        let topMargin: string = String(backGroundBarHeight - (backGroundBarHeight * ratio));

        // console.log(key + " Height=" + barHeight + "px and margin-top=" + topMargin);
        return {"height": barHeight + "px", "margin-top": topMargin + "px", "z-index": zIndex};
    }

    public getQuizImg(): string {
        if (this.quiz.hasStar) {
            return "img-bar-gold-star";
        } else {
            return "img-bar-checkmark";
        }
    }

    public getFluencyImg(): string {
        if (this.fluency.recordingBadge === RecordingBadge.GOLDEN) {
            return "img-bar-gold-star";
        } else if (this.fluency.recordingBadge === RecordingBadge.SILVER) {
            return "img-bar-silver-star";
        } else {
            return "img-bar-checkmark";
        }
    }

    public getPreviewImg(): string {
        if (this.preview.hasStar) {
            return "img-bar-gold-star";
        } else {
            return "img-bar-checkmark";
        }
    }

    public getSectionInfo(): void {
        if (this.report) {
            let sd:ISelectionDescription = this.report.storyDescription;
            let goal = sd.fluencyGoal;

            this.completedDate = this.report.selectionReportDate;
            this.maxPoint = 0;
            this.earnedPoint = 0;

            for (let i = 0; i < this.SECTIONS.length; i++) {
                let key = this.SECTIONS[i];
                if (key === "preview") {
                    this.preview = new PreviewReportItems(this.report.preview.correctCount, sd.taqCount, this.report.preview.point,
                    this.report.preview.hasStar);
                    this.maxPoint += this.preview.goal;
                    this.earnedPoint += this.preview.point;
               } else if (key === "fluency") {
                    this.report.fluency.reviewWords = this.reportsService.filterOutDuplicateWords(this.report.fluency.reviewWords);
                    this.fluency = new FluencyReportItems(this.report.fluency.highestWcpm, goal, this.report.fluency.point, this.report.fluency.reviewWords,
                    this.report.fluency.recordingBadge);
                    this.maxPoint += this.fluency.goal;
                    this.earnedPoint += this.fluency.point;
               } else if (key === "quiz") {
                    this.quiz = new QuizReportItems(this.report.quiz.highestCorrectCount,
                        sd.quizCount,
                        this.report.quiz.point,
                        this.report.quiz.hasStar);
                    this.maxPoint += this.quiz.goal;
                    this.earnedPoint += this.quiz.point;
               }
            }
        }
    }

    public rowStripe(index:number):string {
        return UtilitiesService.rowStripe(index);
    }

    private setupListeners(): void {
        let activityModalEventOpen = this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent)).subscribe((event:ActivityModalEvent) => {
            if (event.status === ACTIVITY_STATUS.modal_open && event.data.type === this.modalType) {
                if (event.data instanceof ReportEventData) {
                    this.isAutomatic = event.data.isAutomatic();
                    this.openModal(event.data);
                }
            }
        });
        this.subscriptions.push(activityModalEventOpen);
    }

    public wordColorClass(wordColor:ReviewWordColor):ContentCssClass {
        return ContentFormatter.wordColorToCssClass(wordColor);
    }

    public getReviewWords(): ReviewWord[] {
        return this.reportsService.outputBufferedList(12, this.report.fluency.reviewWords);
    }

    public getReviewWordsTable(index: number, list: ReviewWord[] = []): ReviewWord[] {
        return this.reportsService.getReviewWordsTable(index, list);
    }

    public showLandscapeWordsPractice(): boolean {
        return window.innerWidth > 1200;
    }

    public maxPointsBufferNeeded(): {"pl-3": boolean, "pl-1": boolean} {
        return {
            "pl-3": window.innerWidth < 850,
            "pl-1": window.innerWidth >= 850
        };
    }

    public getSelectedBookCoverImage() {
        return this.config.introImageUrl(this.report.storyDescription.coverImage);
    }

}
